import { Ref, ref } from "vue";
import { defineStore } from "pinia";
import { TasksService } from "@/services/v2/tasks";
import { redirectToTasksView } from "@/utils/v2";
import { useSitesStore } from "@/stores/v2";
import {
  Task,
  ConfiguredVaccine,
  VaccineConfiguration,
  Tank,
  TankGroup,
} from "@/types/v2";
import { isVaccinationTask, storeTanks } from "./utils";
import { TasksStorageService } from "@/services/v2/tasks/storage";
import { convertStringHumanReadable } from "@/utils/v2";

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const parseScheduledOn = (scheduledOn: string | null): Date | null => {
  if (!scheduledOn || typeof scheduledOn !== "string") {
    return null;
  }

  const normalized = scheduledOn.trim();
  if (!normalized) {
    return null;
  }

  // Treat YYYY-MM-DD as local date to avoid UTC day-shift issues.
  if (DATE_ONLY_REGEX.test(normalized)) {
    const [year, month, day] = normalized.split("-").map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isTaskActive = (task: Task): boolean => {
  return task.status === "pending" || task.status === "in_progress";
};

const isFutureScheduledTask = (task: Task, now = new Date()): boolean => {
  const scheduledDate = parseScheduledOn(task.scheduledOn);
  if (!scheduledDate) {
    return false;
  }

  return scheduledDate.getTime() > now.getTime();
};

const getTaskObjectiveId = (task: Task): string => {
  const objective = task.objective as unknown;

  if (typeof objective === "string") {
    return objective;
  }

  if (objective && typeof objective === "object" && "id" in objective) {
    const id = (objective as { id?: unknown }).id;
    if (typeof id === "string" && id.trim()) {
      return id.trim();
    }
  }

  return "unknown";
};

const getTaskObjectiveLabel = (task: Task): string => {
  const objective = task.objective as unknown;

  if (objective && typeof objective === "object" && "label" in objective) {
    const label = (objective as { label?: unknown }).label;
    if (typeof label === "string" && label.trim()) {
      return label.trim();
    }
  }

  return convertStringHumanReadable(getTaskObjectiveId(task));
};

const filterTasksByStatus = (tasks: Task[], activeFilter: string): Task[] => {
  if (activeFilter === "pending") {
    const now = new Date();
    return tasks.filter((task) => {
      return isTaskActive(task) && !isFutureScheduledTask(task, now);
    });
  }

  if (activeFilter === "finished") {
    return tasks.filter(
      (task) =>
        task.status === "finished" &&
        (task.type === "regular" || task.type === "irregular")
    );
  }

  if (activeFilter === "scheduled") {
    const now = new Date();
    return tasks.filter((task) => {
      return isTaskActive(task) && isFutureScheduledTask(task, now);
    });
  }

  return tasks;
};

export const useTasksStore = defineStore({
  id: "tasks",
  state: () => ({
    tasks: ref([]) as Ref<Task[]>,
    activeFilter: "pending" as string,
    activeTypeFilter: "all" as string,
    selectedTask: null as null | Task,
    activeTask: null as null | Task,
    taskFilters: [
      { name: "Available", term: "pending" },
      { name: "Scheduled", term: "scheduled" },
      { name: "Completed", term: "finished" },
    ],
  }),

  getters: {
    getTasks(state) {
      return state.tasks;
    },
    getTasksByStatus(state) {
      return filterTasksByStatus(state.tasks, state.activeFilter);
    },
    getTasksByActiveFilters(state) {
      const statusFilteredTasks = filterTasksByStatus(state.tasks, state.activeFilter);
      if (state.activeTypeFilter === "all") {
        return statusFilteredTasks;
      }

      return statusFilteredTasks.filter((task) => {
        return getTaskObjectiveId(task) === state.activeTypeFilter;
      });
    },
    getActiveTask(state) {
      return state.activeTask;
    },
    getActiveFilter(state) {
      return state.activeFilter;
    },
    getActiveTypeFilter(state) {
      return state.activeTypeFilter;
    },
    getFilters(state) {
      return state.taskFilters;
    },
    getTypeFilters(state) {
      const statusFilteredTasks = filterTasksByStatus(state.tasks, state.activeFilter);
      const uniqueTypesMap = new Map<string, string>();

      statusFilteredTasks.forEach((task) => {
        const objectiveId = getTaskObjectiveId(task);
        if (!objectiveId || uniqueTypesMap.has(objectiveId)) {
          return;
        }

        uniqueTypesMap.set(objectiveId, getTaskObjectiveLabel(task));
      });

      const objectiveFilters = Array.from(uniqueTypesMap.entries())
        .map(([term, name]) => ({ name, term }))
        .sort((first, second) => first.name.localeCompare(second.name));

      return [{ name: "All Types", term: "all" }, ...objectiveFilters];
    },
  },
  actions: {
    async init(siteKey: string) {
      const tasks: Task[] = await TasksService.getTasksBySiteKey(siteKey);
      if (tasks) this.setTasks(tasks);
    },

    setTasks(tasks: Task[]) {
      return (this.tasks = tasks);
    },

    setActiveFilter(term: string) {
      this.activeFilter = term;
      this.activeTypeFilter = "all";
    },

    setActiveTypeFilter(term: string) {
      this.activeTypeFilter = term;
    },

    setSelectedTask(task: Task) {
      this.selectedTask = task;
    },

    async setActiveTask(task: Task) {
      this.selectedTask = null;
      this.activeTask = { ...task, comment: null };
      await TasksStorageService.storeActiveTask(task);
      if (isVaccinationTask(task)) {
        storeTanks(task);
      }
    },

    updateTaskTankData(updatedTank: Tank) {
      let tanks: Tank[] = [];

      if (
        this.activeTask &&
        isVaccinationTask(this.activeTask) &&
        this.activeTask?.details?.tanks
      ) {
        tanks = this.activeTask?.details?.tanks;
      }
      const tankIndex = tanks.findIndex((t) => t.key === updatedTank.key);

      if (tankIndex !== -1) {
        tanks[tankIndex] = { ...tanks[tankIndex], ...updatedTank };
      } else {
        tanks.push(updatedTank);
      }

      // if (this.activeTask) {
      //   // Prepare the updated task
      //   const updatedTask: Task = {
      //     ...this.activeTask,
      //     tanks: tanks,
      //   };

      //   Promise.resolve(TasksService.updateSingleTaskInIndexDB(updatedTask))
      //     .then((updatedTaskFromService) => {
      //       if (
      //         this.activeTask &&
      //         updatedTaskFromService &&
      //         isVaccinationTask(this.activeTask)
      //       ) {
      //         // Update the original active task with the changes
      //         this.activeTask.tanks = updatedTaskFromService.tanks;
      //       } else {
      //         console.error("Failed to update task in indexDB.");
      //       }
      //     })
      //     .catch((error) => {
      //       console.error("Error updating task in indexDB:", error);
      //     });
      // }
    },

    updateActiveTaskAllTanksData(tanks: Tank[]) {
      if (this.activeTask) {
        this.activeTask = {
          ...this.activeTask,
          progressData: { ...this.activeTask.progressData, tanks: tanks },
        };
      }
    },
    updateActiveVacinationTaskConfiguration(
      configuration: VaccineConfiguration
    ) {
      if (this.activeTask && isVaccinationTask(this.activeTask)) {
        const configurationToUpdate =
          this.activeTask.details?.configurations?.find(
            (c) => c.key === configuration.key
          );

        if (this.activeTask.details?.configurations && configurationToUpdate) {
          const configurations = this.activeTask.details?.configurations.map(
            (c) => {
              if (c.key === configuration.key) {
                return configuration;
              }
              return c;
            }
          );

          this.activeTask = {
            ...this.activeTask,
            details: {
              ...this.activeTask.details,
              configurations: configurations,
            },
          };
        }
      }
    },
    createConfiguration(
      id: number,
      key: string,
      vaccines: ConfiguredVaccine[],
      sourceTank: Tank,
      sourceGroup: TankGroup,
      targetAmount: number,
      destinationTank: Tank,
      destinationGroup: TankGroup
    ) {
      const newConfiguration: VaccineConfiguration = {
        key,
        vaccines,
        sourceTank,
        sourceGroup,
        targetAmount,
        destinationTank,
        destinationGroup,
      };

      if (
        this.activeTask &&
        isVaccinationTask(this.activeTask) &&
        this.activeTask.details?.configurations
      ) {
        this.activeTask.details?.configurations.push(newConfiguration);
      }

      return newConfiguration;
    },
    async updateConfigurationItem(
      configIndex: number,
      nestedIndex: number,
      key: string,
      item: any
    ) {
      if (this.activeTask && !isVaccinationTask(this.activeTask)) {
        return null;
      }
      const configuration =
        this.activeTask?.details?.configurations?.[configIndex];

      if (configuration && configuration.vaccines && nestedIndex !== -1) {
        const nestedItem: any = configuration.vaccines[nestedIndex];
        if (nestedItem && key in nestedItem) {
          if (key === "vaccine" || key === "method") {
            nestedItem[key] = { ...item };
          } else {
            nestedItem[key] = item;
          }
        }
      } else {
        const config = configuration as { [index: string]: any };
        config[key] = item;
      }

      // Update the activeTask with the modified configuration
      const updatedTask = { ...this.activeTask };

      if (
        configIndex !== undefined &&
        updatedTask &&
        updatedTask.details?.configurations
      ) {
        updatedTask.details.configurations[configIndex] =
          configuration as VaccineConfiguration;
      }

      // Call updateSingleTaskInIndexDB to persist the changes
      await TasksStorageService.storeActiveTask(updatedTask as Task);
    },
    addVaccineToConfig(configurationId: string, vaccineProperties: any) {
      const newVaccine = { ...vaccineProperties };

      if (
        this.activeTask &&
        isVaccinationTask(this.activeTask) &&
        this.activeTask.details?.configurations
      ) {
        const configuration = this.activeTask.details.configurations.find(
          (config) => config.key === configurationId
        );

        if (configuration && configuration.vaccines) {
          configuration.vaccines.push(newVaccine);
        }
      }

      return newVaccine;
    },

    async updateVaccinationData(
      configKey: string,
      data: {
        vaccinated: number;
        weight: number;
        comment: string;
      }
    ) {
      if (!this.activeTask || !isVaccinationTask(this.activeTask)) return;

      const configuration = this.activeTask.details.configurations.find(
        (config) => config.key === configKey
      );

      if (configuration) {
        const progressData = {
          ...data,
          timestamp: new Date().toISOString(),
        };

        configuration.progressData = [
          ...(configuration.progressData || []),
          progressData,
        ];
      }
    },

    async removeProgressDataItem(configIndex: number, dataIndex: number) {
      if (
        this.activeTask &&
        isVaccinationTask(this.activeTask) &&
        this.activeTask.details?.configurations
      ) {
        const configuration =
          this.activeTask.details.configurations[configIndex];
        if (configuration && configuration.progressData) {
          // Remove the item at dataIndex from progressData
          configuration.progressData.splice(dataIndex, 1);

          // Update the activeTask with the modified configuration
          const updatedTask = { ...this.activeTask };
          if (updatedTask && updatedTask.details?.configurations) {
            updatedTask.details.configurations[configIndex] = configuration;
          }

          await TasksStorageService.storeActiveTask(updatedTask);
        }
      }
    },
    getTaskById(taskKey: string) {
      return this.tasks.find((t) => t.key === taskKey);
    },
    completeTask(task: Task) {
      const { activeSiteId } = useSitesStore();
      this.activeTask = task;
      const updatedTasks = this.tasks.map((t: Task) =>
        t.key === task.key ? task : t
      );

      Promise.resolve(TasksStorageService.storeTasks(updatedTasks))
        .then(() => {
          // Update state & DB with new data
          this.tasks = updatedTasks;
          // Reset progress state
          this.resetActiveTaskState();
          // Ensure that tasks have been successfully updated before removing the active task
          // TasksStorageService.();

          if (activeSiteId) {
            redirectToTasksView();
          }
        })
        .catch((error) => {
          console.error("Error updating tasks:", error);
        });
    },
    resetActiveTaskState() {
      this.selectedTask = null;
      this.activeTask = null;
    },
  },
});
